import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import { getUserScreensCount } from '../../../../services/getUserScreensCount';
import { getUserScreens } from '../../../../services/getUserScreens';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../../services/userSession';
import { SCREEN_DISPLAY_LIMITS, truncateDisplay } from '../../../../utils/screenDisplayUtils';
import { isScreenActive } from '../../../../utils/screenActiveStatus';
import SkeletonText from '../../../../components/SkeletonText/SkeletonText';
import '../../../Advertiser/AdvertiserDashboard/ScreenMapView/ScreenMapView.css';
import './UserDashboardHome.css';

function displayMeta(value) {
    if (value === undefined || value === null || String(value).trim() === '') {
        return 'N/A';
    }
    return truncateDisplay(String(value), SCREEN_DISPLAY_LIMITS.gridMetric);
}

function ScreenCardSkeleton() {
    return (
        <div className="screenCard screenCardSkeleton inactive" aria-busy="true">
            <div className="screenCardHeader">
                <div className="screenCardStatus">
                    <SkeletonText width="72px" height="14px" />
                </div>
                <SkeletonText width="56px" height="14px" />
            </div>
            <div className="screenCardContent">
                <SkeletonText className="screenLocation" width="70%" height="1.35em" />
            </div>
            <div className="screenCardMetrics">
                <div className="metric">
                    <span className="metricLabel">Brand</span>
                    <SkeletonText className="metricValue" width="60%" />
                </div>
                <div className="metric">
                    <span className="metricLabel">Type</span>
                    <SkeletonText className="metricValue" width="50%" />
                </div>
                <div className="metric">
                    <span className="metricLabel">Last Active</span>
                    <SkeletonText className="metricValue" width="80%" />
                </div>
            </div>
            <div className="screenCardFooter">
                <SkeletonText width="88px" height="36px" />
            </div>
        </div>
    );
}

export default function UserDashboardHome() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state || (() => {
        try {
            const savedState = sessionStorage.getItem('userDashboardState');
            return savedState ? JSON.parse(savedState) : getUserSession();
        } catch (e) {
            console.error(e);
            return getUserSession();
        }
    })();

    const [screenCount, setScreenCount] = useState(null);
    const [screens, setScreens] = useState([]);
    const [screensLoading, setScreensLoading] = useState(true);
    const [error, setError] = useState(null);
    const token = getUserAccessToken() || secureLocalStorage.getItem('tk') || state?.token;

    useEffect(() => {
        if (location.state) {
            sessionStorage.setItem('userDashboardState', JSON.stringify(location.state));
            persistUserSession(location.state);
        }
    }, [location.state]);

    useEffect(() => {
        if (!token || !state?.userId) {
            navigate('/');
            return;
        }

        let cancelled = false;

        setScreensLoading(true);
        setError(null);

        Promise.allSettled([
            getUserScreensCount(token, state.userId),
            getUserScreens(token, state.userId),
        ])
            .then(([countResult, screensResult]) => {
                if (cancelled) return;

                const count = countResult.status === 'fulfilled'
                    ? Number(countResult.value.data?.screenCount ?? 0)
                    : 0;
                setScreenCount(count);

                if (screensResult.status === 'fulfilled') {
                    const list = screensResult.value.data?.screens || [];
                    setScreens(list);
                    setError(null);
                    return;
                }

                setScreens([]);
                if (count > 0) {
                    setError('Failed to load your screens. Please try again.');
                } else {
                    setError(null);
                }
            })
            .finally(() => {
                if (!cancelled) setScreensLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [navigate, state?.userId, token]);

    const activeScreensCount = screens.filter((s) => isScreenActive(s)).length;
    const placeholderCount = screensLoading
        ? Math.max(screenCount ?? 0, screens.length, 1)
        : 0;

    function openScreenInfo(screenId) {
        navigate('/user/userDashboard/screenInfo', {
            state: {
                phoneNumber: state.phoneNumber,
                token,
                userId: state.userId,
                screenId,
            },
        });
        persistUserSession({ screenId });
    }

    return (
        <div className="userDashboardHomeContainer screenMapViewContainer">
            <div className="screenMapViewHeader">
                <div className="screenMapViewTitle">
                    <h1>Your Screens</h1>
                    <span className="screenMapViewSubtitle">
                        Manage and monitor the screens linked to your account
                    </span>
                </div>
                <div className="screenMapViewControls">
                    <div className="screenMapViewStats">
                        <div className="stat">
                            <span className="statLabel">Active Screens</span>
                            <span className="statValue">
                                {screensLoading ? (
                                    <SkeletonText width="48px" height="1em" />
                                ) : (
                                    `${activeScreensCount}/${screens.length}`
                                )}
                            </span>
                        </div>
                        <div className="stat">
                            <span className="statLabel">Total</span>
                            <span className="statValue">
                                {screenCount === null ? (
                                    <SkeletonText width="32px" height="1em" />
                                ) : (
                                    screenCount
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="screenGridContainer">
                {error && (
                    <div className="noScreensMessage" style={{ color: '#C52F00', gridColumn: '1 / -1' }}>
                        <span>{error}</span>
                    </div>
                )}

                {screensLoading && placeholderCount > 0
                    ? Array.from({ length: placeholderCount }, (_, index) => (
                        <ScreenCardSkeleton key={`skeleton-${index}`} />
                    ))
                    : null}

                {!screensLoading && screens.length > 0
                    ? screens.map((screen) => {
                        const isActive = isScreenActive(screen);
                        const addressTitle = truncateDisplay(
                            screen.screenName || screen.address || `Screen ${screen.screenId}`,
                            SCREEN_DISPLAY_LIMITS.addressLine,
                        );
                        const lastActiveShort = screen.lastActive
                            ? truncateDisplay(new Date(screen.lastActive).toLocaleString(), 36)
                            : 'Never';

                        return (
                            <div
                                key={screen.screenId}
                                className={`screenCard ${isActive ? 'active' : 'inactive'}`}
                            >
                                <div className="screenCardHeader">
                                    <div className="screenCardStatus">
                                        <span className={`statusIndicator ${isActive ? 'active' : 'inactive'}`} />
                                        <span className="statusText">{isActive ? 'Active' : 'Inactive'}</span>
                                    </div>
                                    <span className="screenId">#{screen.screenId}</span>
                                </div>

                                <div className="screenCardContent">
                                    <h3 className="screenLocation">{addressTitle}</h3>
                                </div>

                                <div className="screenCardMetrics">
                                    <div className="metric">
                                        <span className="metricLabel">Brand</span>
                                        <span className="metricValue">{displayMeta(screen.brand ?? screen.Brand)}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metricLabel">Type</span>
                                        <span className="metricValue">{displayMeta(screen.type ?? screen.Type)}</span>
                                    </div>
                                    <div className="metric">
                                        <span className="metricLabel">Last Active</span>
                                        <span
                                            className="metricValue"
                                            title={screen.lastActive ? new Date(screen.lastActive).toLocaleString() : ''}
                                        >
                                            {lastActiveShort}
                                        </span>
                                    </div>
                                </div>

                                <div className="screenCardFooter">
                                    <button
                                        type="button"
                                        className="screenCardMoreInfoBtn"
                                        onClick={() => openScreenInfo(screen.screenId)}
                                    >
                                        More info
                                    </button>
                                </div>
                            </div>
                        );
                    })
                    : null}

                {!screensLoading && screens.length === 0 && !error ? (
                    <div className="noScreensMessage">
                        <span>No screens linked yet</span>
                    </div>
                ) : null}

                {!screensLoading ? (
                    <div
                        className="screenCard userDashboardAddScreenCard"
                        onClick={() => navigate('/user/linkNewScreen/', { state: { phoneNumber: state.phoneNumber } })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                navigate('/user/linkNewScreen/', { state: { phoneNumber: state.phoneNumber } });
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="userDashboardAddScreenInner">
                            <span className="userDashboardAddScreenPlus">+</span>
                            <span className="userDashboardAddScreenLabel">Link a screen</span>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
